import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

type Invitation = {
	id: string;
	email: string;
	role: string;
	expiresAt: string;
};

const INVITE_ROLES = ["viewer", "member", "admin"] as const;

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function InviteSection() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [invitations, setInvitations] = useState<Invitation[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [email, setEmail] = useState("");
	const [role, setRole] = useState<string>("member");
	const [submitting, setSubmitting] = useState(false);
	const [inviteLink, setInviteLink] = useState<string | null>(null);

	const fetchInvitations = useCallback(
		async (signal?: AbortSignal) => {
			try {
				setError(null);
				const res = await fetch(`/api/tenant/${workspaceSlug}/invitations`, {
					credentials: "include",
					signal,
				});
				if (!res.ok) {
					throw new Error(`Failed to load invitations (${res.status})`);
				}
				const data = (await res.json()) as { invitations: Invitation[] };
				setInvitations(data.invitations);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(
					err instanceof Error ? err.message : "Failed to load invitations",
				);
			} finally {
				setLoading(false);
			}
		},
		[workspaceSlug],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchInvitations(controller.signal);
		return () => controller.abort();
	}, [fetchInvitations]);

	async function handleInvite(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSubmitting(true);
		setError(null);
		setInviteLink(null);

		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/invitations`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email, role }),
			});
			if (!res.ok) {
				throw new Error(`Failed to send invitation (${res.status})`);
			}
			const data = (await res.json()) as { inviteLink: string };
			setInviteLink(data.inviteLink);
			setEmail("");
			setRole("member");
			await fetchInvitations();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to send invitation",
			);
		} finally {
			setSubmitting(false);
		}
	}

	async function handleRevoke(id: string) {
		try {
			setError(null);
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/invitations/${id}`,
				{
					method: "DELETE",
					credentials: "include",
				},
			);
			if (!res.ok) {
				throw new Error(`Failed to revoke invitation (${res.status})`);
			}
			await fetchInvitations();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to revoke invitation",
			);
		}
	}

	return (
		<div data-testid="invite-section">
			<h3 className="text-base font-semibold text-gray-900 mb-3">
				Invite a member
			</h3>

			<form
				onSubmit={handleInvite}
				className="flex items-end gap-3 mb-4"
				data-testid="invite-form"
			>
				<div className="flex-1">
					<label
						htmlFor="invite-email"
						className="block text-xs font-medium text-gray-600 mb-1"
					>
						Email
					</label>
					<input
						id="invite-email"
						type="email"
						required
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="colleague@example.com"
						className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						data-testid="invite-email-input"
					/>
				</div>
				<div>
					<label
						htmlFor="invite-role"
						className="block text-xs font-medium text-gray-600 mb-1"
					>
						Role
					</label>
					<select
						id="invite-role"
						value={role}
						onChange={(e) => setRole(e.target.value)}
						className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
						data-testid="invite-role-select"
					>
						{INVITE_ROLES.map((r) => (
							<option key={r} value={r}>
								{r}
							</option>
						))}
					</select>
				</div>
				<button
					type="submit"
					disabled={submitting}
					className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
					data-testid="invite-submit-btn"
				>
					{submitting ? "Sending..." : "Send Invite"}
				</button>
			</form>

			{inviteLink && (
				<div
					className="bg-green-50 border border-green-200 rounded-md p-3 mb-4"
					data-testid="invite-link-container"
				>
					<p className="text-xs font-medium text-green-800 mb-1">
						Invite link created — share it with the invitee:
					</p>
					<input
						type="text"
						readOnly
						value={inviteLink}
						onClick={(e) => (e.target as HTMLInputElement).select()}
						className="w-full text-sm bg-white border border-green-300 rounded px-2 py-1 font-mono text-green-900 focus:outline-none"
						data-testid="invite-link-input"
					/>
				</div>
			)}

			{error && (
				<div
					className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4"
					data-testid="invite-error"
				>
					{error}
				</div>
			)}

			<h3 className="text-base font-semibold text-gray-900 mb-3 mt-6">
				Pending invitations
			</h3>

			{loading ? (
				<div
					className="text-sm text-gray-500"
					data-testid="invitations-loading"
				>
					Loading invitations...
				</div>
			) : invitations.length === 0 ? (
				<p className="text-sm text-gray-400" data-testid="invitations-empty">
					No pending invitations.
				</p>
			) : (
				<table
					className="w-full text-sm text-left"
					data-testid="invitations-table"
				>
					<thead>
						<tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
							<th className="py-2 pr-4 font-medium">Email</th>
							<th className="py-2 pr-4 font-medium">Role</th>
							<th className="py-2 pr-4 font-medium">Expires</th>
							<th className="py-2 font-medium">Actions</th>
						</tr>
					</thead>
					<tbody>
						{invitations.map((inv) => (
							<tr
								key={inv.id}
								className="border-b border-gray-100"
								data-testid="invitation-row"
							>
								<td className="py-3 pr-4 text-gray-900">{inv.email}</td>
								<td className="py-3 pr-4">
									<span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
										{inv.role}
									</span>
								</td>
								<td className="py-3 pr-4 text-gray-500">
									{formatDate(inv.expiresAt)}
								</td>
								<td className="py-3">
									<button
										type="button"
										onClick={() => handleRevoke(inv.id)}
										className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
										data-testid="revoke-invite-btn"
									>
										Revoke
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}
